import { useForm, useStore } from '@tanstack/react-form'
import type { ClientResponseDto } from '@trellis/api'
import { RichTextEditor } from '@trellis/roots/data'
import { DatePicker, FormFooter, FormSection } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import {
  CreateQuoteSchema,
  TAX_TYPE_DEFAULTS,
  UpdateQuoteSchema,
  type CreateQuote,
  type DocumentDiscountType,
  type DocumentTaxType,
  type InvoiceTaxType,
  type UpdateQuote,
} from '@trellis/shared'
import { FileText, Info, StickyNote, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { ClientCombobox } from '@/components/client-combobox'
import { DiscountSection } from '@/components/discount-section'
import {
  DocumentLineItemsSection,
  type SortableLineItem,
  withId,
} from '@/components/document-line-items-section'
import { TermsTemplatePicker } from '@/components/terms-templates'
import { useClientQuery } from '@/hooks'
import { useDocumentTotals } from '@/hooks/useDocumentTotals'
import { useLineItemValidation } from '@/hooks/useLineItemValidation'
import {
  useDefaultDueDate,
  usePaymentTermsDefault,
  useTaxDefaults,
} from '@/hooks/useOrganization'
import { reportInvalidFields } from '@/lib/analytics'
import { createLineItem } from '@/requests/line-items'

import type { LineItemRowValue } from '../../invoices/(components)/LineItemRow'

async function saveAdHocRowsToRegistry(
  rows: SortableLineItem[],
): Promise<SortableLineItem[]> {
  return Promise.all(
    rows.map(async (row) => {
      if (row.type !== 'adhoc' || !row.saveToRegistry) return row
      try {
        const result = await createLineItem({
          title: row.title,
          description: row.description ?? undefined,
          unitPrice: row.unitPrice,
          unitType: row.unitType,
          quantity: row.quantity,
          discount: row.discount,
          discountType: row.discountType,
          taxable: row.taxable,
        })
        if (result.error) return row
        const created = result.data
        return {
          _id: row._id,
          type: 'registry' as const,
          lineItemId: created.id,
          title: created.title,
          unitPrice: created.unitPrice,
          unitType: created.unitType,
          description: created.description,
          taxable: created.taxable,
          quantity: row.quantity,
          discountType: row.discountType,
          discount: row.discount,
        } satisfies SortableLineItem
      } catch {
        return row
      }
    }),
  )
}

export enum QuoteFormMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export interface QuoteFormSummary {
  selectedClientId: string
  selectedClient: ClientResponseDto | null
  subtotal: number
  discountType: DocumentDiscountType
  discount: number
  discountAmount: number
  taxAmount: number
  total: number
  taxType: InvoiceTaxType
  taxRate: number
}

interface QuoteFormProps {
  mode: QuoteFormMode
  // taxType/taxRate excluded: fixed from org tax settings (create) or the existing
  // document (edit) — see fixedTaxType/fixedTaxRate — never sourced from this prop.
  defaultValues?: Partial<Omit<CreateQuote, 'taxType' | 'taxRate'>>
  initialLineItems?: LineItemRowValue[]
  onSubmit: (data: CreateQuote | UpdateQuote, lineItems: LineItemRowValue[]) => void
  isSubmitting?: boolean
  onSummaryChange?: (summary: QuoteFormSummary) => void
  /** Bubbles the full client object up whenever one is picked or created in the combobox. */
  onClientSelect?: (client: ClientResponseDto) => void
  /** Org's default terms template body — applied once on create if terms is still empty. */
  defaultTermsTemplateBody?: string
  /** The quote's current tax type/rate in edit mode — fixed, displayed read-only. */
  existingTax?: { taxType: InvoiceTaxType; taxRate: number }
}

export function QuoteForm({
  mode,
  defaultValues,
  initialLineItems,
  onSubmit,
  isSubmitting,
  onSummaryChange,
  onClientSelect,
  defaultTermsTemplateBody,
  existingTax,
}: QuoteFormProps) {
  const [selectedClient, setSelectedClient] = useState<ClientResponseDto | null>(null)
  const [lineItems, setLineItems] = useState<SortableLineItem[]>(() =>
    (initialLineItems ?? []).map(withId),
  )
  const { errorsByRow: lineItemErrors, validate: validateLineItems } =
    useLineItemValidation()
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const { defaultTaxType, defaultTaxRate } = useTaxDefaults()
  const paymentTermsDays = usePaymentTermsDefault()
  const defaultExpiresAt = useDefaultDueDate()

  /**
   * Tax is fixed, not user-editable on this form: set once from the org's tax settings on
   * create, or carried through unchanged from the existing quote on edit — see TaxTab.tsx
   * for the only place tax type/rate can be changed. Both are fractions (e.g. 0.1 for
   * 10%), matching the stored/response representation.
   */
  const fixedTaxType: InvoiceTaxType =
    mode === QuoteFormMode.CREATE
      ? (defaultTaxType ?? 'NONE')
      : (existingTax?.taxType ?? 'NONE')
  const fixedTaxRate: number =
    mode === QuoteFormMode.CREATE
      ? (defaultTaxRate ?? (defaultTaxType ? TAX_TYPE_DEFAULTS[defaultTaxType] : 0))
      : (existingTax?.taxRate ?? 0)

  const form = useForm({
    defaultValues: {
      clientId: defaultValues?.clientId ?? '',
      discount: defaultValues?.discount ?? 0,
      discountType: defaultValues?.discountType ?? ('NONE' as DocumentDiscountType),
      notes: defaultValues?.notes ?? '',
      terms: defaultValues?.terms ?? '',
      expiresAt: defaultValues?.expiresAt
        ? defaultValues.expiresAt.substring(0, 10)
        : mode === QuoteFormMode.CREATE
          ? defaultExpiresAt
          : '',
    },
    onSubmitInvalid: ({ formApi }) =>
      reportInvalidFields('quote', formApi.state.fieldMeta),
    onSubmit: async ({ value }) => {
      if (!validateLineItems(lineItems)) return

      const savedLineItems = await saveAdHocRowsToRegistry(lineItems)
      setLineItems(savedLineItems)
      const rawLineItems = savedLineItems.map(({ _id: _, ...lineItem }) => lineItem)
      try {
        if (mode === QuoteFormMode.CREATE) {
          const processedLineItems = rawLineItems.map((lineItem) => {
            if (lineItem.type === 'registry') {
              return {
                lineItemId: lineItem.lineItemId,
                quantity: lineItem.quantity,
                discount: isNaN(lineItem.discount) ? undefined : lineItem.discount,
                discountType: lineItem.discountType,
              }
            }
            const {
              type: _type,
              saveToRegistry: _saveToRegistry,
              ...adHocFields
            } = lineItem
            return {
              ...adHocFields,
              description: adHocFields.description ?? undefined,
              discount: isNaN(adHocFields.discount) ? undefined : adHocFields.discount,
            }
          })
          onSubmit(
            CreateQuoteSchema.parse({
              ...value,
              // fixedTaxType always comes from the org's tax settings, which the write
              // path already restricts to DocumentTaxType — see TaxTab.tsx.
              taxType: fixedTaxType as DocumentTaxType,
              taxRate: fixedTaxRate,
              discount: isNaN(value.discount) ? 0 : value.discount,
              expiresAt: value.expiresAt ? new Date(value.expiresAt).toISOString() : '',
              lineItems: processedLineItems.length > 0 ? processedLineItems : undefined,
            }),
            rawLineItems,
          )
        } else {
          // taxType/taxRate omitted: fixed and not user-editable on this form, so the
          // update never touches them — see fixedTaxType/fixedTaxRate.
          const { clientId: _clientId, ...editValues } = value
          onSubmit(
            UpdateQuoteSchema.parse({
              ...editValues,
              discount: isNaN(editValues.discount) ? 0 : editValues.discount,
              expiresAt: editValues.expiresAt
                ? new Date(editValues.expiresAt).toISOString()
                : '',
            }),
            rawLineItems,
          )
        }
      } catch (caughtError) {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Please check the form'
        toast.error('Unable to save quote', { description: message })
      }
    },
  })

  const selectedClientId = useStore(form.store, (s) => s.values.clientId)
  const currentDiscount = useStore(form.store, (s) => s.values.discount)
  const currentDiscountType = useStore(form.store, (s) => s.values.discountType)

  const { data: prefilledClientResponse } = useClientQuery(
    selectedClientId,
    mode === QuoteFormMode.CREATE && !selectedClient,
  )

  /**
   * Hydrates the sidebar client panel when clientId is prefilled but not chosen via the
   * combobox — e.g. New Quote from a client's profile. Edit mode already resolves the
   * selected client from its own `useClientsQuery` list fetch, so this stays gated to
   * creation to avoid a redundant `GET /clients/:id`.
   */
  useEffect(() => {
    if (!selectedClient && prefilledClientResponse?.data) {
      setSelectedClient(prefilledClientResponse.data)
    }
  }, [selectedClient, prefilledClientResponse])

  // Pre-fill terms from the org's default template on new documents only, and only once
  useEffect(() => {
    if (mode !== QuoteFormMode.CREATE || !defaultTermsTemplateBody) return
    if (form.getFieldValue('terms')) return
    form.setFieldValue('terms', defaultTermsTemplateBody)
  }, [mode, defaultTermsTemplateBody]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-checks only rows already flagged invalid, so a fixed field's error clears
  // immediately. Rows added since the last save attempt are untouched and stay
  // unflagged until the next save.
  function handleLineItemsChange(nextLineItems: SortableLineItem[]): void {
    setLineItems(nextLineItems)
    const previouslyInvalidIds = new Set(Object.keys(lineItemErrors))
    if (!hasAttemptedSubmit || previouslyInvalidIds.size === 0) return
    validateLineItems(
      nextLineItems.filter((lineItem) => previouslyInvalidIds.has(lineItem._id)),
    )
  }

  const { subtotal, discountAmount, taxAmount, total } = useDocumentTotals(
    lineItems,
    fixedTaxType,
    fixedTaxRate * 100,
    'AUD',
    currentDiscount,
    currentDiscountType,
  )
  const discountExceedsSubtotal =
    currentDiscountType === 'FIXED' &&
    !isNaN(currentDiscount) &&
    currentDiscount > subtotal

  useEffect(() => {
    onSummaryChange?.({
      selectedClientId,
      selectedClient,
      subtotal,
      discountType: currentDiscountType,
      discount: currentDiscount,
      discountAmount,
      taxAmount,
      total,
      taxType: fixedTaxType,
      taxRate: fixedTaxRate,
    })
  }, [
    selectedClientId,
    selectedClient,
    subtotal,
    currentDiscountType,
    currentDiscount,
    discountAmount,
    taxAmount,
    total,
    fixedTaxType,
    fixedTaxRate,
    onSummaryChange,
  ])

  return (
    <form
      noValidate
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault()
        submitEvent.stopPropagation()
        setHasAttemptedSubmit(true)
        // TanStack skips its onSubmit callback when another field is invalid, so this
        // runs unconditionally to avoid hiding line item errors in that case.
        if (!validateLineItems(lineItems)) {
          toast.error('Fix the highlighted line items before saving')
        }
        void form.handleSubmit()
      }}
      className="space-y-5"
    >
      {mode === QuoteFormMode.CREATE && (
        <FormSection
          icon={UserRound}
          title="Quoting to"
          description="Who's being quoted?"
        >
          <form.Field
            name="clientId"
            validators={{ onSubmit: CreateQuoteSchema.shape.clientId }}
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Client</Label>
                <ClientCombobox
                  id={field.name}
                  value={field.state.value}
                  onChange={(client) => {
                    field.handleChange(client.id)
                    setSelectedClient(client)
                    onClientSelect?.(client)
                  }}
                />
                {field.state.meta.errors.length > 0 ? (
                  <Typography variant="body" color="destructive-text">
                    {field.state.meta.errors[0]?.message}
                  </Typography>
                ) : null}
              </div>
            )}
          />
        </FormSection>
      )}

      <FormSection
        icon={FileText}
        title="Quote details"
        description="Expiry date for this quote."
      >
        <form.Field
          name="expiresAt"
          children={(field) => (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor={field.name}>Expiry Date</Label>
                {mode === QuoteFormMode.CREATE && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="More information"
                        >
                          <Info className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Defaults to Net {paymentTermsDays} days. Change this in Payment
                        Options.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <DatePicker
                id={field.name}
                value={field.state.value}
                onChange={field.handleChange}
              />
            </div>
          )}
        />
      </FormSection>

      <DocumentLineItemsSection
        value={lineItems}
        onChange={handleLineItemsChange}
        currency="AUD"
        errors={lineItemErrors}
        isEditMode={mode === QuoteFormMode.EDIT}
      />

      <DiscountSection
        discount={{
          discountType: currentDiscountType,
          discount: currentDiscount,
          onChange: (discountType, discount) => {
            form.setFieldValue('discountType', discountType)
            form.setFieldValue('discount', discount)
          },
          exceedsSubtotal: discountExceedsSubtotal,
        }}
        currency="AUD"
      />

      <FormSection
        icon={StickyNote}
        title="Notes & terms"
        collapsible
        defaultOpen={Boolean(defaultValues?.notes || defaultValues?.terms)}
        description="Visible to the client on the quote."
      >
        <form.Field
          name="notes"
          children={(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>Notes</Label>
              <RichTextEditor
                id={field.name}
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="Optional notes"
              />
            </div>
          )}
        />

        <form.Field
          name="terms"
          children={(field) => (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor={field.name}>Terms</Label>
                <TermsTemplatePicker onSelect={field.handleChange} />
              </div>
              <RichTextEditor
                id={field.name}
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="Optional terms and conditions"
              />
            </div>
          )}
        />
      </FormSection>

      <FormFooter
        statusText={
          mode === QuoteFormMode.EDIT
            ? 'Changes will update this quote.'
            : 'Nothing saved yet.'
        }
        actions={[
          {
            label: isSubmitting ? 'Saving...' : 'Save Quote',
            type: 'submit',
            disabled: isSubmitting,
          },
        ]}
      />
    </form>
  )
}
